import { type GenericMap } from "@client/types";
import { type HttpApiResponse } from "@client/types/Api";
import { type ReduxAction } from "@client/types/ReduxAction";
import { isFunction } from "lodash";
import { type AnyAction, type Dispatch } from "redux";
import { type ThunkDispatch } from "redux-thunk";

export enum Actions {
  Unathenticated = "UNATHENTICATED"
}

interface BeginActionFnArgs<TParams, TState> {
  dispatch: ThunkDispatch<TState, any, AnyAction>;
  getState: () => TState;
  data: TParams;
  params: TParams;
}
type BeginActionFn<TParams, TState> = (args: BeginActionFnArgs<TParams, TState>) => any;
type BeginActionType<TParams, TState> = string | BeginActionFn<TParams, TState>;

interface FailureActionFnArgs<TState, TParams> {
  dispatch: ThunkDispatch<TState, any, AnyAction>;
  getState: () => TState;
  error: string;
  params: TParams;
  formFieldErrors?: GenericMap<string>;
}
export type FailureActionFn<TState, TParams> = (args: FailureActionFnArgs<TState, TParams>) => any;
export type FailureActionType<TState, TParams> = string | FailureActionFn<TState, TParams>;

interface SuccessActionFnArgs<TResult, TParams, TState> {
  dispatch: ThunkDispatch<TState, any, AnyAction>;
  getState: () => TState;
  res: TResult;
  params: TParams;
}
type SuccessActionFn<TResult, TParams, TState> = (args: SuccessActionFnArgs<TResult, TParams, TState>) => any;
type SuccessActionType<TResult, TParams, TState> = string | SuccessActionFn<TResult, TParams, TState>;

export interface AsyncActionTypes<TParams, TResult, TState> {
  begin: BeginActionType<TParams, TState> | BeginActionType<TParams, TState>[];
  failure: FailureActionType<TState, TParams> | FailureActionType<TState, TParams>[];
  success: SuccessActionType<TResult, TParams, TState> | SuccessActionType<TResult, TParams, TState>[];
}

export interface AsyncActionArgs<TState, TArgs, TResult> {
  types: AsyncActionTypes<TArgs, TResult, TState>;
  action: (state: TState, dispatch: Dispatch<AnyAction>, params?: TArgs) => Promise<TResult>;
}

export type FailureActionData<T = any> = {
  error: string;
  formFieldErrors?: GenericMap<any>;
  data: T;
};

export const asyncAction =
  <TState, TArgs, TResult>({ types, action }: AsyncActionArgs<TState, TArgs, TResult>) =>
  (params?: TArgs) =>
  async (dispatch: ThunkDispatch<TState, any, AnyAction>, getState: () => TState): Promise<TResult> => {
    const promise = action(getState(), dispatch, params);

    const begin = Array.isArray(types.begin) ? types.begin : [types.begin];
    begin.forEach((type) => {
      if (isFunction(type)) {
        (type as BeginActionFn<TArgs | undefined, TState>)({ dispatch, getState, params, data: params });
      } else {
        dispatch({ type, params, data: params });
      }
    });

    try {
      const res = await promise;

      // NOTE: this is very important for dynamic reducers. When a page loads we must set entities and reducers in one update.
      const success = Array.isArray(types.success) ? types.success : [types.success];
      for (let i = 0; i < success.length; i++) {
        const type = success[i];

        if (isFunction(type)) {
          const successRes = (type as SuccessActionFn<TResult, TArgs | undefined, TState>)({
            res,
            params,
            getState,
            dispatch
          });
          if (successRes instanceof Promise) {
            await successRes;
          }
        } else {
          dispatch({ type, data: res, params });
        }
      }

      return res;
    } catch (e) {
      console.trace(e);

      const failure = Array.isArray(types.failure) ? types.failure : [types.failure];
      failure.forEach((type) => {
        const error = e instanceof Error ? { error: e.message, params } : e ?? { error: "Unknown error" };
        const formFieldErrors = e?.formFieldErrors;

        if (isFunction(type)) {
          (type as FailureActionFn<TState, TArgs | undefined>)({ error, dispatch, formFieldErrors, getState, params });
        } else {
          dispatch({ formFieldErrors, type, data: error, params });
        }
      });

      throw e;
    }
  };

export interface ApiActionArgs<TState, TArgs, TResponse, TResult = TResponse> {
  types: AsyncActionTypes<TArgs, TResult, TState>;
  action: (
    state: TState,
    dispatch: ThunkDispatch<any, any, any>,
    params: TArgs | undefined
  ) => Promise<HttpApiResponse<TResponse>>;
  mapResponse?: (state: TState, resp: TResponse, args?: TArgs) => TResult;
  onSuccess?: (dispatch: Dispatch<any>) => void;
}

export const apiAction =
  <TState, TArgs, TResponse, TResult = TResponse>({
    types,
    action,
    mapResponse,
    onSuccess
  }: ApiActionArgs<TState, TArgs, TResponse, TResult>) =>
  (params: TArgs) =>
  (dispatch: ThunkDispatch<TState, TArgs, ReduxAction<any>>): Promise<TResult> => {
    const aa = asyncAction<TState, TArgs, TResult>({
      types,
      action: (state): Promise<TResult> =>
        action(state, dispatch, params).then((res: HttpApiResponse<TResponse>) => {
          if (res.status === 401 && res.response?.error === "_next_") {
            dispatch({
              type: Actions.Unathenticated,
              data: {}
            });

            throw new Error("Unathenticated");
          }
          if (res.status >= 400) throw res.response;

          if (!res.response) throw new Error("Unexpected response");

          if (res.response.status !== "success") {
            throw new Error(res.response.error);
          }

          const result = mapResponse
            ? mapResponse!(state, res.response.data, params)
            : ((<unknown>res.response.data) as TResult);
          onSuccess && onSuccess(dispatch);
          return result;
        })
    });

    return dispatch(aa(params));
  };
